/**
 * /admin/data-room/queue - manual curation queue.
 *
 * Per CHAP-236 (Phase 1R #212). Admin sees every pending_review
 * dataroom_extractions row across tenants with the source document
 * + the proposed jsonPayload. Approve runs the worker's
 * applyToProductionTables; reject marks the row resolved with no
 * production-table changes.
 *
 * Per CHAP-238 ("doesn't crowd the app"): this IS the surface where
 * raw documents cross into structured tenant data. Everywhere else
 * dashboards read structured rows directly.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api.js';

interface ExtractionRow {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantDisplayName: string;
  documentId: string;
  documentName: string;
  documentFilename: string;
  documentCategory: string | null;
  extractionType: string;
  jsonPayload: unknown;
  status: string;
  createdAt: string | null;
}

interface QueueResponse {
  extractions: ExtractionRow[];
  count: number;
}

const CATEGORY_BADGE: Record<string, { label: string; cls: string }> = {
  member_list: { label: 'Member list', cls: 'bg-orange-100 text-orange-900' },
  recording: { label: 'Recording', cls: 'bg-purple-100 text-purple-900' },
  transcript: { label: 'Transcript', cls: 'bg-red-100 text-red-900' },
  contract: { label: 'Contract', cls: 'bg-blue-100 text-blue-800' },
  financial: { label: 'Financial', cls: 'bg-green-100 text-green-800' },
};

export default function AdminDataRoomQueue() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<QueueResponse, Error>({
    queryKey: ['admin-dataroom-queue'],
    queryFn: () => apiGet<QueueResponse>('/api/admin/dataroom/queue'),
    refetchInterval: 30_000,
  });

  const approve = useMutation({
    mutationFn: (id: string) => apiPost<{ id: string; status: string; applied: unknown[] }>(
      `/api/admin/dataroom/queue/${encodeURIComponent(id)}/approve`,
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-dataroom-queue'] }),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<{ id: string; status: string }>(
        `/api/admin/dataroom/queue/${encodeURIComponent(id)}/reject`,
        { reason },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-dataroom-queue'] }),
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Data Room Curation Queue</h2>
        <p className="text-neutral text-sm mt-1">
          Pending extractions waiting for admin review. Approving applies
          the proposed changes to tenant tables (alumni records, financial
          model, etc.). Rejecting marks the row resolved without altering
          tenant data. Per CHAP-238, this is the only surface where raw
          documents cross into structured app data.
        </p>
      </header>

      {(approve.error || reject.error) && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          {approve.error?.message || reject.error?.message}
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading queue…
        </div>
      )}
      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load queue: {error.message}
        </div>
      )}
      {data && data.count === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Queue is empty. Nothing pending review.
        </div>
      )}

      {data && data.count > 0 && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 text-xs text-neutral">
            {data.count} pending extraction{data.count === 1 ? '' : 's'}
          </div>
          <ul className="divide-y divide-gray-100">
            {data.extractions.map((e) => {
              const cat = e.documentCategory ?? 'other';
              const badge = CATEGORY_BADGE[cat] ?? { label: cat, cls: 'bg-gray-100 text-gray-700' };
              const isExpanded = expanded === e.id;
              return (
                <li key={e.id} className="p-3 sm:p-4 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                        <span className="text-navy text-sm font-semibold truncate">{e.documentName}</span>
                        <span className="text-[10px] text-neutral font-mono">{e.tenantSlug}</span>
                      </div>
                      <p className="text-xs text-neutral mt-1 truncate">
                        {e.documentFilename} → worker: <span className="font-mono">{e.extractionType}</span>
                        {e.createdAt && (
                          <span className="ml-2">· {new Date(e.createdAt).toLocaleString()}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setExpanded(isExpanded ? null : e.id)}
                        className="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
                      >
                        {isExpanded ? 'Hide' : 'Preview'}
                      </button>
                      <button
                        type="button"
                        disabled={approve.isPending}
                        onClick={() => approve.mutate(e.id)}
                        className="bg-navy text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-navy/90 disabled:opacity-50"
                      >
                        {approve.isPending && approve.variables === e.id ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={reject.isPending}
                        onClick={() => {
                          const reason = window.prompt('Reason for rejecting this extraction?') ?? '';
                          if (reason.trim().length > 0) reject.mutate({ id: e.id, reason: reason.trim() });
                        }}
                        className="text-xs font-medium px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <pre className="mt-3 text-xs font-mono text-neutral bg-gray-50 rounded px-3 py-2 overflow-x-auto max-h-96">
{JSON.stringify(e.jsonPayload, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
